/**
 * F5 — RED: network/denomination view. Pick a mother church (root of the
 * network), browse its subtree and see the AGGREGATED REPORT (membership +
 * attendance + finances) built with the PURE `subtreeReport` logic of
 * `@agape/shared`, which respects permissions: a member church only counts in
 * the aggregate of the metric whose grant the account holds (the allowed sets
 * come from the SAME `effective_grant`s that govern sync — they never
 * diverge). A network admin with a federated grant (inherit at the mother)
 * sees the whole subtree; a local leader only their church.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  buildChurchForest,
  type ChurchTreeNode,
  type ChurchTreeRow,
  formatMoney,
  getAttendanceSessionsQuery,
  getChurchesQuery,
  getFinanceRecordsQuery,
  getMembershipsQuery,
  type NetworkChurch,
  queryKeys,
  subtreeReport,
} from "@agape/shared";
import type {
  AttendanceSessionRecord,
  ChurchRecord,
  FinanceRecordRecord,
  MembershipRecord,
} from "@agape/powersync";
import { Church as ChurchIcon, KeyRound, Network, Users } from "@fleet/icons";

import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { useDomainQuery, useGrants } from "../use-domain";

function StatTile(props: { label: string; value: string; testId?: string }) {
  return (
    <Card>
      <CardContent class="py-3">
        <p class="text-xs uppercase tracking-wider text-muted-foreground">{props.label}</p>
        <p class="text-lg font-bold tabular-nums" data-testid={props.testId}>
          {props.value}
        </p>
      </CardContent>
    </Card>
  );
}

function SubtreeNode(props: { node: ChurchTreeNode }) {
  return (
    <>
      <Link to="/churches/$churchId" params={{ churchId: props.node.row.id }} class="block">
        <Card
          data-testid="network-church-node"
          data-church-id={props.node.row.id}
          class="transition-colors hover:border-primary/40"
          style={{ "margin-inline-start": `${props.node.depth * 1.25}rem` }}
        >
          <CardContent class="flex items-center gap-3 py-2.5">
            <span class="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
              <ChurchIcon size={14} />
            </span>
            <p class="min-w-0 truncate text-sm font-medium">{props.node.row.name}</p>
          </CardContent>
        </Card>
      </Link>
      <For each={props.node.children}>{(child) => <SubtreeNode node={child} />}</For>
    </>
  );
}

export function NetworkPage() {
  const { t } = useI18n();
  const grants = useGrants();

  const churches = useDomainQuery<ChurchRecord>(
    (uid) => queryKeys.domain.churches(uid),
    (db) => getChurchesQuery(db),
  );
  const memberships = useDomainQuery<MembershipRecord>(
    (uid) => queryKeys.domain.memberships(uid),
    (db) => getMembershipsQuery(db),
  );
  const sessions = useDomainQuery<AttendanceSessionRecord>(
    (uid) => queryKeys.domain.attendanceSessions(uid),
    (db) => getAttendanceSessionsQuery(db),
  );
  const finances = useDomainQuery<FinanceRecordRecord>(
    (uid) => queryKeys.domain.financeRecords(uid),
    (db) => getFinanceRecordsQuery(db),
  );

  const rows = createMemo<NetworkChurch[]>(() =>
    (churches.data ?? []).map((c) => ({
      id: c.id,
      name: c.name ?? "",
      path: c.path ?? c.id,
      parent_id: c.parent_id,
    })),
  );

  // Network root candidates: mother churches (no synced parent) — the natural
  // point from which an inherited grant federates. Default: the first root.
  const roots = createMemo(() => {
    const byId = new Set(rows().map((c) => c.id));
    return rows().filter((c) => !c.parent_id || !byId.has(c.parent_id));
  });
  const [rootId, setRootId] = createSignal("");
  const activeRoot = () => rootId() || roots()[0]?.id || rows()[0]?.id || "";

  const membershipChurchIds = createMemo(
    () => new Set(grants.nodesWith("people", "read", "church")),
  );
  const attendanceChurchIds = createMemo(
    () => new Set(grants.nodesWith("attendance", "read", "church")),
  );
  const financeChurchIds = createMemo(
    () => new Set(grants.nodesWith("finances", "read", "church")),
  );

  const report = createMemo(() =>
    subtreeReport({
      rootId: activeRoot(),
      churches: rows(),
      memberships: (memberships.data ?? []).map((m) => ({
        id: m.id,
        church_id: m.church_id,
        status: m.status,
        baptized: m.baptized,
        serves: m.serves,
        is_leader: m.is_leader,
        is_pastor: m.is_pastor,
        deleted_at: m.deleted_at,
      })),
      sessions: (sessions.data ?? []).map((s) => ({
        id: s.id,
        headcount: s.headcount,
        occurrence_start: s.occurrence_start,
        church_id: s.church_id,
        group_id: s.group_id,
        meeting_id: s.meeting_id,
      })),
      finances: (finances.data ?? []).map((r) => ({
        id: r.id,
        kind: r.kind,
        amount_minor: r.amount_minor,
        currency: r.currency,
        occurred_at: r.occurred_at,
        created_at: r.created_at,
        fund_id: r.fund_id,
        category_id: r.category_id,
        church_id: r.church_id,
      })),
      membershipChurchIds: membershipChurchIds(),
      attendanceChurchIds: attendanceChurchIds(),
      financeChurchIds: financeChurchIds(),
    }),
  );

  const subtreeRows = createMemo<ChurchTreeRow[]>(() => {
    const ids = new Set(report().perChurch.map((c) => c.churchId));
    return rows()
      .filter((c) => ids.has(c.id))
      .map((c) => ({ id: c.id, parent_id: c.parent_id, path: c.path ?? c.id, name: c.name ?? "" }));
  });
  const forest = createMemo(() => buildChurchForest(subtreeRows()));
  const currency = () => report().currency ?? "EUR";

  return (
    <div class="space-y-4" data-testid="network-page">
      <PageSubHeader title={t(msg`Network`)} />
      <p class="text-sm text-muted-foreground">
        {t(
          msg`Manage a network or denomination: navigate the tree of member churches and see the aggregated roll-up of everything your account can reach.`,
        )}
      </p>

      <Show
        when={rows().length > 0}
        fallback={
          <p data-testid="network-empty" class="text-muted-foreground">
            {t(msg`No churches synced to this account yet.`)}
          </p>
        }
      >
        <div class="max-w-sm">
          <Select
            options={rows().map((c) => ({ value: c.id, label: c.name ?? c.id }))}
            value={activeRoot()}
            onChange={setRootId}
            aria-label={t(msg`Network root church`)}
            data-testid="network-root-select"
          />
        </div>

        {/* Totales del subárbol (roll-up respetando permisos). */}
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile
            label={t(msg`Churches`)}
            value={String(report().churchCount)}
            testId="network-total-churches"
          />
          <StatTile
            label={t(msg`Members`)}
            value={String(report().totals.members)}
            testId="network-total-members"
          />
          <StatTile
            label={t(msg`Attendance`)}
            value={String(report().totals.attendanceTotal)}
            testId="network-total-attendance"
          />
          <StatTile
            label={t(msg`Net`)}
            value={formatMoney(report().totals.netMinor, currency())}
            testId="network-total-net"
          />
        </div>

        {/* Navegación del subárbol. */}
        <section class="space-y-2">
          <h3 class="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            <Network size={14} /> {t(msg`Member churches`)}
          </h3>
          <div class="space-y-2" data-testid="network-tree">
            <For each={forest()}>{(node) => <SubtreeNode node={node} />}</For>
          </div>
        </section>

        {/* Desglose por iglesia. */}
        <section class="space-y-2">
          <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t(msg`Per-church breakdown`)}
          </h3>
          <div class="overflow-x-auto">
            <table class="w-full min-w-[32rem] text-sm" data-testid="network-breakdown">
              <thead>
                <tr class="border-b border-border text-left text-xs uppercase text-muted-foreground">
                  <th class="py-2 pr-3 font-medium">{t(msg`Church`)}</th>
                  <th class="py-2 pr-3 text-right font-medium">{t(msg`Members`)}</th>
                  <th class="py-2 pr-3 text-right font-medium">{t(msg`Attendance`)}</th>
                  <th class="py-2 text-right font-medium">{t(msg`Net`)}</th>
                </tr>
              </thead>
              <tbody>
                <For each={report().perChurch}>
                  {(c) => (
                    <tr
                      class="border-b border-border/50"
                      data-testid="network-church-row"
                      data-church-id={c.churchId}
                      data-has-membership={c.hasMembership ? "true" : "false"}
                      data-has-finance={c.hasFinance ? "true" : "false"}
                    >
                      <td class="py-2 pr-3">
                        <span style={{ "padding-inline-start": `${c.depth * 0.75}rem` }}>
                          {c.name}
                        </span>
                      </td>
                      <td class="py-2 pr-3 text-right tabular-nums">
                        <Show when={c.hasMembership} fallback={<span class="opacity-40">—</span>}>
                          {c.members.total}
                        </Show>
                      </td>
                      <td class="py-2 pr-3 text-right tabular-nums">
                        <Show when={c.hasAttendance} fallback={<span class="opacity-40">—</span>}>
                          {c.attendanceTotal}
                        </Show>
                      </td>
                      <td class="py-2 text-right tabular-nums">
                        <Show when={c.hasFinance} fallback={<span class="opacity-40">—</span>}>
                          {formatMoney(c.netMinor, c.currency ?? currency())}
                        </Show>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </section>

        {/* Federación de roles: la madre federa hacia sus hijas con un grant
            heredado. Enlace a /permissions para otorgarlo. */}
        <section
          class="space-y-2 rounded-lg border border-border bg-muted/30 p-4"
          data-testid="network-federation"
        >
          <h3 class="flex items-center gap-2 text-sm font-semibold">
            <Users size={16} /> {t(msg`Federated roles`)}
          </h3>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`A role granted on the network root with inheritance enabled federates to every member church below — a denomination admin gets effective grants across the whole subtree.`,
            )}
          </p>
          <p class="text-sm">
            {t(msg`This network reaches`)}{" "}
            <span class="font-semibold tabular-nums" data-testid="network-federated-count">
              {report().churchCount}
            </span>{" "}
            {t(msg`member churches.`)}
          </p>
          <Link
            to="/permissions"
            class="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            data-testid="network-federate-link"
          >
            <KeyRound size={14} /> {t(msg`Grant a federated role`)}
          </Link>
        </section>
      </Show>
    </div>
  );
}
